import * as React from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LinkRefSources } from 'mangools-commons/lib/constants/LinkRefSources';
import getAppUrl from 'mangools-commons/lib/constants/AppEnvUrl';

import Image from './Image';
import UserPlanCta from './UserPlanCta';
import { ReactNode } from 'react';

type MangoolsNavBarLinkProps = {
  href: string;
  firstLine: string;
  secondLine: string;
  icon: () => ReactNode;
  isActive: boolean;
};

const NavBarLink = ({
  href,
  firstLine,
  secondLine,
  icon,
  isActive,
}: MangoolsNavBarLinkProps) => (
  <div
    className="uk-flex uk-flex-center uk-flex-middle uk-height-1-1 mg-margin-l-15 nav-bar-custom-item"
    style={{ opacity: isActive ? 1 : 0.75 }}
  >
    <a
      href={href}
      style={{ display: 'flex', gap: '10px', textDecoration: 'none' }}
    >
      <div className="uk-flex uk-flex-center" style={{ height: '25px' }}>
        {icon ? icon() : null}
      </div>
      <div className="uk-flex uk-flex-column uk-flex-center">
        <span className="first-line">{firstLine}</span>
        <span className="second-line">{secondLine}</span>
      </div>
    </a>
    <div className="mg-margin-l-15 separator" />
  </div>
);

type MangoolsNavBarProps = {
  appName: string;
  env: string;
  rightEl: React.ReactNode;
  onShowAppPanel?(): void;
  logoDarkSrc?: string;
  logoLightSrc?: string;
  colorScheme?: string;
  userPlanType?: string;
  onAiContentClickAction?(): void;
};

const MangoolsNavBar = ({
  appName,
  env,
  onShowAppPanel,
  rightEl,
  onAiContentClickAction,
  ...restProps
}: MangoolsNavBarProps) => {
  const linkRef = `?ref=header-${appName}`;

  return (
    <nav className="mg-header-mngls uk-flex uk-flex-space-between uk-flex-middle">
      <div className="uk-flex uk-height-1-1">
        <a
          href={`${getAppUrl(LinkRefSources.MANGOOLS_APP)}/${linkRef}`}
          className="mg-main-logo uk-flex uk-flex-middle uk-hidden-small"
        >
          <Image
            lightSrc={restProps.logoLightSrc}
            darkSrc={restProps.logoDarkSrc}
            colorScheme={restProps.colorScheme}
            alt="Mangools logo"
            width="130"
          />
        </a>
        <button
          onClick={onShowAppPanel}
          type="button"
          className="mg-main-logo uk-visible-small uk-flex uk-flex-middle"
        >
          <span className="mg-app-toggle">
            <FontAwesomeIcon icon="bars" aria-label="Tools menu" />
          </span>
          <Image
            lightSrc={restProps.logoLightSrc}
            darkSrc={restProps.logoDarkSrc}
            colorScheme={restProps.colorScheme}
            alt="Mangools logo"
            width="130"
          />
        </button>
        <ul className="mg-nav uk-flex-inline uk-hidden-small">
          <li
            className={classnames('uk-position-relative', {
              ['uk-text-bold']: appName === LinkRefSources.KWFINDER_APP,
            })}
          >
            <NavBarLink
              href={getAppUrl(LinkRefSources.KWFINDER_APP, env) + '/' + linkRef}
              firstLine="KWFinder"
              secondLine="Keyword Research"
              isActive={appName === LinkRefSources.KWFINDER_APP}
              icon={() => (
                <svg
                  width="19px"
                  height="25px"
                  viewBox="0 0 20 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M1.92937 6.68163C1.84497 6.74254 1.77294 6.81961 1.71743 6.90837C1.66193 6.99713 1.62407 7.09581 1.60605 7.19869C1.58802 7.30157 1.59019 7.4066 1.61243 7.50768C1.63467 7.60877 1.67654 7.70389 1.7356 7.78755C1.79041 7.86916 1.86137 7.93809 1.94408 7.99007C2.0268 8.04204 2.11951 8.07596 2.21649 8.08973C2.31347 8.10349 2.41265 8.09681 2.5079 8.07009C2.60315 8.04337 2.69244 7.99719 2.77024 7.93439L4.99857 6.34365L5.28741 6.74445C5.20302 6.80536 5.13098 6.88243 5.07548 6.97119C5.01997 7.05995 4.98211 7.15863 4.96409 7.26151C4.94606 7.36439 4.94823 7.46942 4.97047 7.5705C4.99271 7.67159 5.03458 7.76672 5.09365 7.85037C5.14845 7.93198 5.21941 8.00091 5.30212 8.05289C5.38484 8.10486 5.47755 8.13878 5.57453 8.15255C5.67151 8.16631 5.7707 8.15963 5.86594 8.13291C5.96119 8.10619 6.05048 8.06001 6.12828 7.99722L6.17722 8.02215L12.45 17.2422C11.9575 17.8277 11.6207 18.5302 11.473 19.2804C11.3792 19.7871 11.385 20.3054 11.4901 20.8052C11.5951 21.305 11.7973 21.7763 12.085 22.1918C12.3696 22.6097 12.7381 22.961 13.1668 23.2231C13.5956 23.4853 14.0754 23.6527 14.5757 23.7147C15.071 23.8194 15.5854 23.812 16.0826 23.693C16.5799 23.574 17.0479 23.3462 17.4539 23.0257C17.8875 22.7315 18.2599 22.3521 18.5484 21.9107C18.837 21.4692 19.0357 20.9748 19.1325 20.4574C19.304 19.4346 19.085 18.3928 18.5206 17.546C17.9422 16.7072 17.0564 16.1419 16.0555 15.9728C15.2697 15.8338 14.4551 15.9511 13.7274 16.308L7.4289 7.13835C7.51044 7.08075 7.57988 7.00701 7.6329 6.9217C7.68593 6.83639 7.72141 6.74133 7.73717 6.64238C7.75293 6.54344 7.74862 6.44273 7.72451 6.34647C7.7004 6.25021 7.65701 6.16046 7.59701 6.08277C7.48784 5.91125 7.31571 5.79181 7.11777 5.75025C6.91984 5.7087 6.71198 5.74835 6.53909 5.86065L3.82312 1.87703C3.76469 1.79314 3.69032 1.72215 3.60424 1.66811C3.51817 1.61407 3.42208 1.57805 3.32146 1.5621C3.22084 1.54615 3.11767 1.55058 3.01782 1.57514C2.91798 1.5997 2.82342 1.64391 2.73955 1.70525C2.65568 1.76659 2.58414 1.84385 2.52901 1.93262C2.47388 2.02139 2.43624 2.11994 2.41825 2.22263C2.40026 2.32532 2.40226 2.43015 2.42414 2.53113C2.44603 2.63212 2.48736 2.72727 2.54579 2.81116L2.64365 2.86104L0.415318 4.45178C0.333773 4.50939 0.264341 4.58312 0.211316 4.66843C0.158291 4.75374 0.122802 4.84881 0.107044 4.94775C0.0912865 5.04669 0.0955951 5.14741 0.119703 5.24367C0.143812 5.33992 0.187207 5.42967 0.247205 5.50737C0.356373 5.67889 0.528508 5.79832 0.726444 5.83988C0.92438 5.88144 1.13223 5.84178 1.30512 5.72948L3.48452 4.1138L4.13441 5.01562L1.92937 6.68163ZM17.4437 18.3287C17.8249 18.9067 17.9706 19.6162 17.8509 20.3115C17.7986 20.6578 17.6764 20.9909 17.4923 21.2899C17.3081 21.5889 17.0658 21.8472 16.7807 22.0486C16.2034 22.4495 15.5021 22.6139 14.8207 22.5081C14.1416 22.3659 13.5464 21.9667 13.1542 21.3906C12.762 20.8145 12.6018 20.104 12.7057 19.4013C12.8294 18.7279 13.2037 18.1234 13.7502 17.7145C14.0276 17.4983 14.3467 17.3451 14.6852 17.2658C15.0238 17.1864 15.3736 17.1828 15.7102 17.2551C16.0574 17.3064 16.3896 17.4278 16.6872 17.6121C16.9848 17.7965 17.242 18.0401 17.4437 18.3287Z"
                    fill="#FFBE02"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    // eslint-disable-next-line max-len
                    d="M15.1583 23.4354C16.9918 23.4354 18.4782 21.9491 18.4782 20.1156C18.4782 18.2821 16.9918 16.7958 15.1583 16.7958C13.3248 16.7958 11.8385 18.2821 11.8385 20.1156C11.8385 21.9491 13.3248 23.4354 15.1583 23.4354ZM15.1583 21.5382C15.9441 21.5382 16.5811 20.9012 16.5811 20.1154C16.5811 19.3296 15.9441 18.6926 15.1583 18.6926C14.3726 18.6926 13.7356 19.3296 13.7356 20.1154C13.7356 20.9012 14.3726 21.5382 15.1583 21.5382Z"
                    fill="#FFBE02"
                  />
                  <rect
                    x="4.25037"
                    y="10.5068"
                    width="3.79409"
                    height="1.6364"
                    rx="0.818199"
                    transform="rotate(-36.3278 4.25037 10.5068)"
                    fill="#FFBE02"
                  />
                </svg>
              )}
            />
          </li>
          <li
            className={classnames('uk-position-relative', {
              ['uk-text-bold']: appName === LinkRefSources.SERPCHECKER_APP,
            })}
          >
            <NavBarLink
              href={
                getAppUrl(LinkRefSources.SERPCHECKER_APP, env) + '/' + linkRef
              }
              firstLine="SERPChecker"
              secondLine="SERP Analysis"
              isActive={appName === LinkRefSources.SERPCHECKER_APP}
              icon={() => (
                <svg
                  width="10"
                  height="15"
                  viewBox="0 0 10 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_1697_523)">
                    <path
                      // eslint-disable-next-line max-len
                      d="M4.85294 0C3.56586 0 2.3315 0.493231 1.42139 1.37119C0.511291 2.24915 0 3.43991 0 4.68153C0 9.17197 4.85294 15 4.85294 15C4.85294 15 9.70588 9.17197 9.70588 4.68153C9.70588 3.43991 9.19459 2.24915 8.28449 1.37119C7.37439 0.493231 6.14002 0 4.85294 0ZM4.85294 7.26115C4.33385 7.26115 3.82642 7.11266 3.39482 6.83445C2.96321 6.55625 2.62682 6.16083 2.42817 5.69819C2.22953 5.23556 2.17755 4.72649 2.27882 4.23536C2.38009 3.74423 2.63005 3.2931 2.9971 2.93901C3.36415 2.58493 3.8318 2.34379 4.34092 2.2461C4.85003 2.14841 5.37774 2.19855 5.85731 2.39018C6.33689 2.58181 6.74678 2.90632 7.03517 3.32268C7.32356 3.73904 7.47749 4.22855 7.47749 4.7293C7.47749 5.40079 7.20098 6.04477 6.70878 6.51959C6.21658 6.9944 5.54902 7.26115 4.85294 7.26115Z"
                      fill="#FFBE02"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1697_523">
                      <rect width="9.70588" height="15" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              )}
            />
          </li>
          <li
            className={classnames('uk-position-relative', {
              ['uk-text-bold']: appName === LinkRefSources.SERPWATCHER_APP,
            })}
          >
            <NavBarLink
              href={
                getAppUrl(LinkRefSources.SERPWATCHER_APP, env) +
                '/trackings' +
                linkRef
              }
              firstLine="SERPWatcher"
              secondLine="Rank Tracking"
              isActive={appName === LinkRefSources.SERPWATCHER_APP}
              icon={() => (
                <svg
                  width="19px"
                  height="25px"
                  viewBox="0 0 21 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_1697_532)">
                    <path
                      // eslint-disable-next-line max-len
                      d="M5.96216 9.51709L10.4951 9.75318V14.4278L8.07754 16.3165L5.96216 9.51709Z"
                      fill="#FFBE02"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M9.54726 13.4657C9.54726 14.3488 9.28799 15.2121 8.80223 15.9464C8.31648 16.6807 7.62605 17.253 6.81827 17.591C6.01049 17.9289 5.12163 18.0174 4.2641 17.8451C3.40656 17.6728 2.61886 17.2475 2.00061 16.623C1.38236 15.9986 0.96133 15.203 0.790755 14.3368C0.620181 13.4706 0.707726 12.5728 1.04232 11.7569C1.37691 10.941 1.94353 10.2437 2.67051 9.75301C3.3975 9.26237 4.2522 9.00049 5.12654 9.00049C6.29898 9.00049 7.42341 9.47093 8.25246 10.3083C9.0815 11.1457 9.54726 12.2814 9.54726 13.4657Z"
                      fill="#FFBE02"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M8.85646 13.4653C8.85646 14.2104 8.6377 14.9388 8.22784 15.5584C7.81798 16.1779 7.23544 16.6608 6.55387 16.946C5.87231 17.2311 5.12233 17.3057 4.39879 17.1604C3.67524 17.015 3.01062 16.6562 2.48897 16.1293C1.96733 15.6024 1.61208 14.9311 1.46816 14.2003C1.32423 13.4694 1.3981 12.7119 1.68041 12.0235C1.96273 11.3351 2.44081 10.7467 3.0542 10.3327C3.6676 9.91871 4.38875 9.69775 5.12647 9.69775C6.11573 9.69775 7.06446 10.0947 7.76397 10.8012C8.46348 11.5078 8.85646 12.4661 8.85646 13.4653Z"
                      fill="#2B2A2E"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M15.7836 9.51709L10.4951 9.75318V14.4278L13.3156 16.3165L15.7836 9.51709Z"
                      fill="#FFBE02"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M11.6195 13.4653C11.6127 14.3583 11.869 15.2331 12.356 15.9785C12.8429 16.7238 13.5383 17.306 14.3538 17.6509C15.1693 17.9958 16.0679 18.0879 16.9354 17.9154C17.8028 17.7428 18.5998 17.3136 19.225 16.6821C19.8501 16.0507 20.2752 15.2456 20.446 14.3695C20.6168 13.4933 20.5256 12.5856 20.1842 11.7619C19.8427 10.9382 19.2663 10.2358 18.5284 9.74398C17.7904 9.25217 16.9243 8.99324 16.0402 9.00013C14.8678 9.00013 13.7434 9.47057 12.9143 10.308C12.0853 11.1453 11.6195 12.2811 11.6195 13.4653Z"
                      fill="#FFBE02"
                    />
                    <path
                      // eslint-disable-next-line max-len
                      d="M12.3102 13.4653C12.3102 14.2104 12.5289 14.9388 12.9388 15.5584C13.3487 16.1779 13.9312 16.6608 14.6128 16.946C15.2943 17.2311 16.0443 17.3057 16.7678 17.1604C17.4914 17.015 18.156 16.6562 18.6777 16.1293C19.1993 15.6024 19.5546 14.9311 19.6985 14.2003C19.8424 13.4694 19.7685 12.7119 19.4862 12.0235C19.2039 11.3351 18.7258 10.7467 18.1124 10.3327C17.499 9.91871 16.7779 9.69775 16.0402 9.69775C15.0509 9.69775 14.1022 10.0947 13.4027 10.8012C12.7032 11.5078 12.3102 12.4661 12.3102 13.4653Z"
                      fill="#2B2A2E"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1697_532">
                      <rect
                        width="19.824"
                        height="25"
                        fill="white"
                        transform="translate(0.705811)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              )}
            />
          </li>
          <li
            className={classnames('uk-position-relative', {
              ['uk-text-bold']: appName === LinkRefSources.LINKMINER_APP,
            })}
          >
            <NavBarLink
              href={
                getAppUrl(LinkRefSources.LINKMINER_APP, env) + '/' + linkRef
              }
              firstLine="LinkMiner"
              secondLine="Backlink Checker"
              isActive={appName === LinkRefSources.LINKMINER_APP}
              icon={() => (
                <svg
                  width="15"
                  height="25"
                  viewBox="0 0 15 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M8.41497 10.2148L7.7129 10.9176C7.68245 10.9482 7.65451 10.9808 7.62803 11.0144C7.62003 11.0244 7.61278 11.035 7.60512 11.0454C7.58683 11.0702 7.5697 11.0957 7.55363 11.1219C7.54679 11.1332 7.53966 11.1445 7.53329 11.156C7.51546 11.1874 7.49939 11.2196 7.48495 11.2527C7.48238 11.2585 7.47934 11.2638 7.47701 11.2695C7.46058 11.3087 7.44661 11.3489 7.43492 11.3899C7.4341 11.3928 7.4334 11.3959 7.43276 11.3989C7.75223 11.4899 8.04506 11.6618 8.28387 11.9024C8.63146 12.2512 8.83393 12.7151 8.85397 13.2088C8.85397 13.2102 8.85438 13.2114 8.85438 13.2128C8.85438 13.2136 8.85438 13.2142 8.85438 13.2149C8.85438 13.2152 8.85438 13.2153 8.85438 13.2153C8.87817 13.7634 8.66916 14.3021 8.28072 14.6904L7.36829 15.6049C7.36677 15.6064 7.36473 15.6074 7.36321 15.6091L5.34015 17.6353C4.96988 18.0068 4.47705 18.2116 3.95254 18.2116C3.42733 18.2116 2.93333 18.0061 2.56155 17.6329C2.19093 17.2604 1.98718 16.7649 1.98783 16.2375C1.98835 15.7102 2.19298 15.2152 2.56389 14.8434L4.39757 13.0066C4.39097 12.9856 4.38641 12.9642 4.3801 12.9431C4.36759 12.9011 4.35654 12.8586 4.34526 12.816C4.32416 12.7373 4.30523 12.6579 4.28857 12.5778C4.27928 12.5328 4.27028 12.4879 4.26244 12.4428C4.24789 12.3589 4.23673 12.2743 4.22702 12.1892C4.22247 12.1494 4.21668 12.1098 4.21329 12.0697C4.20265 11.9449 4.19605 11.8194 4.19605 11.6928C4.19616 11.5987 4.1999 11.5046 4.20622 11.4108C4.2082 11.3793 4.21212 11.3479 4.21481 11.3165C4.22042 11.2541 4.22621 11.1916 4.23439 11.1297C4.23567 11.1197 4.2362 11.1095 4.23755 11.0994L1.53351 13.8075C0.197225 15.1457 0.195063 17.3254 1.5289 18.6666C2.17533 19.317 3.03603 19.6754 3.95254 19.6754C4.86561 19.6754 5.72427 19.3188 6.37058 18.6714L9.3115 15.7272C9.55225 15.4866 9.75483 15.213 9.91404 14.9136C10.2982 14.1895 10.4094 13.3602 10.2398 12.5704C10.2006 12.3882 10.1464 12.208 10.0773 12.0317C9.90568 11.5946 9.64997 11.203 9.31688 10.8681C9.05042 10.5998 8.74801 10.3809 8.41497 10.2148Z"
                    fill="#FFBE02"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M5.10282 11.468C5.09738 11.5455 5.09411 11.6231 5.09399 11.701C5.09399 12.1402 5.17413 12.5665 5.33217 12.9677C5.50319 13.4033 5.75954 13.7948 6.09473 14.1318C6.36038 14.3999 6.66249 14.6188 6.99547 14.7847L7.6976 14.0815C7.72846 14.0507 7.75663 14.0177 7.78322 13.9838C7.79164 13.9733 7.79912 13.9622 7.8069 13.9515C7.82437 13.9272 7.84144 13.9022 7.85698 13.8767C7.86476 13.8636 7.87241 13.8504 7.87972 13.8372C7.89381 13.8117 7.90667 13.7856 7.91865 13.7591C7.92461 13.746 7.93092 13.7331 7.93618 13.7198C7.95009 13.686 7.9619 13.6515 7.97236 13.6164C7.97388 13.6111 7.9761 13.606 7.97745 13.6007C7.6592 13.5098 7.36673 13.3383 7.12733 13.0981C6.78418 12.7538 6.58283 12.2986 6.5574 11.8133L6.5574 11.813C6.55705 11.8075 6.556 11.802 6.55588 11.7962C6.55331 11.7654 6.55279 11.7337 6.55279 11.7033C6.55413 11.1754 6.75893 10.6804 7.12955 10.3087L10.0706 7.36421C10.4424 6.99263 10.9355 6.78819 11.4592 6.78819C11.9843 6.78819 12.4783 6.99368 12.8501 7.36679C13.615 8.13698 13.6131 9.3876 12.8467 10.1553L11.0119 11.9934C11.0207 12.0211 11.0276 12.049 11.0358 12.0769C11.0478 12.1172 11.0599 12.1576 11.0705 12.1985C11.08 12.234 11.0881 12.2699 11.0966 12.3056C11.1066 12.3474 11.1164 12.3891 11.1251 12.4312C11.1323 12.4664 11.1386 12.5023 11.145 12.5378C11.1527 12.5803 11.1601 12.6226 11.1666 12.6652C11.1718 12.7009 11.1763 12.7368 11.1807 12.7727C11.1859 12.8152 11.1909 12.8575 11.1952 12.9001C11.1984 12.9363 11.201 12.9725 11.2034 13.0088C11.2062 13.051 11.2089 13.0932 11.2106 13.1353C11.212 13.1723 11.2126 13.209 11.2133 13.2459C11.2137 13.2874 11.2139 13.3291 11.2133 13.3706C11.2126 13.408 11.2116 13.4453 11.2099 13.4826C11.2083 13.5236 11.2061 13.5647 11.2032 13.6057C11.2005 13.6433 11.1973 13.6807 11.1936 13.7183C11.1898 13.759 11.1853 13.7994 11.1803 13.8397C11.1778 13.8595 11.1765 13.8792 11.1738 13.8988L13.8771 11.1912C14.5236 10.5445 14.8799 9.68229 14.8809 8.7632C14.882 7.84422 14.5276 6.98125 13.8829 6.33298C13.2363 5.68243 12.3756 5.32422 11.4592 5.32422C10.5457 5.32422 9.68652 5.68078 9.04032 6.32847L6.09929 9.27251C5.85808 9.51483 5.65445 9.78847 5.49477 10.0859C5.49477 10.0859 5.49459 10.086 5.49459 10.0862C5.26928 10.5098 5.13584 10.9834 5.10282 11.468Z"
                    fill="#FFBE02"
                  />
                </svg>
              )}
            />
          </li>
          <li
            className={classnames('uk-position-relative', {
              ['uk-text-bold']: appName === LinkRefSources.SITEPROFILER_APP,
            })}
          >
            <NavBarLink
              href={
                getAppUrl(LinkRefSources.SITEPROFILER_APP, env) + '/' + linkRef
              }
              firstLine="SiteProfiler"
              secondLine="Site Explorer"
              isActive={appName === LinkRefSources.SITEPROFILER_APP}
              icon={() => (
                <svg
                  width="18"
                  height="25"
                  viewBox="0 0 18 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M10.5894 8.1031L1.25745 17.4351L4.4697 20.6473L13.8017 11.3154L10.5894 8.1031Z"
                    fill="#FFBE02"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M3.62568 20.5534C3.68418 20.6119 3.75363 20.6583 3.83007 20.6899C3.90651 20.7216 3.98844 20.7379 4.07117 20.7379C4.15391 20.7379 4.23584 20.7216 4.31227 20.6899C4.38871 20.6583 4.45817 20.6119 4.51667 20.5534C4.57517 20.4949 4.62158 20.4254 4.65324 20.349C4.6849 20.2725 4.7012 20.1906 4.7012 20.1079C4.7012 20.0251 4.6849 19.9432 4.65324 19.8668C4.62158 19.7903 4.57517 19.7209 4.51667 19.6624L2.21885 17.3646C2.16286 17.3018 2.09422 17.2515 2.01745 17.2171C1.94067 17.1827 1.85749 17.1649 1.77335 17.1649C1.68922 17.1649 1.60604 17.1827 1.52926 17.2171C1.45249 17.2515 1.38385 17.3018 1.32786 17.3646C1.26506 17.4206 1.21481 17.4892 1.18041 17.566C1.146 17.6427 1.12821 17.7259 1.12821 17.8101C1.12821 17.8942 1.146 17.9774 1.18041 18.0542C1.21481 18.1309 1.26506 18.1996 1.32786 18.2556L3.62568 20.5534Z"
                    fill="#FFBE02"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M17.5531 9.20496L15.1615 11.5966C15.0672 11.691 14.9548 11.7655 14.8311 11.8156C14.7074 11.8657 14.5749 11.8904 14.4414 11.8882C14.308 11.886 14.1763 11.857 14.0543 11.8028C13.9323 11.7487 13.8224 11.6706 13.7313 11.5731L10.2376 8.0795C10.1402 7.98832 10.062 7.87845 10.0079 7.75644C9.95377 7.63444 9.92475 7.50279 9.92256 7.36933C9.92037 7.23588 9.94506 7.10335 9.99516 6.97963C10.0453 6.85592 10.1197 6.74355 10.2142 6.64922L12.6058 4.25762C12.5589 4.30451 17.5297 9.3222 17.5531 9.20496Z"
                    fill="#FFBE02"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M12.6762 4.1872L11.9493 4.91406L16.8967 9.86141L17.6235 9.13455L12.6762 4.1872Z"
                    fill="#FFBE02"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M9.90938 11.8314C10.0089 11.9309 10.0648 12.0658 10.0648 12.2066C10.0648 12.3473 10.0089 12.4822 9.90938 12.5817L8.76047 13.7306C8.66098 13.8301 8.52603 13.886 8.38532 13.886C8.24461 13.886 8.10966 13.8301 8.01016 13.7306C7.91067 13.6311 7.85477 13.4962 7.85477 13.3555C7.85477 13.2148 7.91067 13.0798 8.01016 12.9803L9.15907 11.8314C9.25857 11.7319 9.39352 11.676 9.53423 11.676C9.67494 11.676 9.80988 11.7319 9.90938 11.8314Z"
                    fill="#2B2A2E"
                  />
                  <path
                    // eslint-disable-next-line max-len
                    d="M12.8637 3.9999L12.6293 4.23438L17.5766 9.18172L17.8111 8.94725L12.8637 3.9999Z"
                    fill="#2B2A2E"
                  />
                </svg>
              )}
            />
          </li>
          {process.env.CONTENT_GENERATOR_TOP_MENU && (
            <li
              className={classnames('uk-position-relative content-generator', {
                ['uk-text-bold']:
                  appName === LinkRefSources.CONTENTGENERATOR_APP,
              })}
            >
              <a
                href={
                  getAppUrl(LinkRefSources.CONTENTGENERATOR_APP, env) +
                  '/' +
                  linkRef
                }
                onClick={onAiContentClickAction}
              >
                ContentMaster AI
              </a>
            </li>
          )}
        </ul>
      </div>
      <div className="uk-flex uk-flex-middle">
        <UserPlanCta userPlanType={restProps.userPlanType} appName={appName} />
        {rightEl}
      </div>
    </nav>
  );
};

export default MangoolsNavBar;
